import { useEffect, useState } from "react";
import { businessesApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { Mail, Copy, Check, Save, Edit2, Zap } from "lucide-react";
import { toast } from "sonner";

const ALIAS = "dochub@brotherscoffee.co.za";
const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/inbound/resend`;

export default function InboundSettings() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [copied, setCopied] = useState(null);
  const [editing, setEditing] = useState(null);
  const [prefix, setPrefix] = useState("");

  const load = () => businessesApi.list().then(setBusinesses);
  useEffect(() => { load(); }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied");
    setTimeout(() => setCopied(null), 1500);
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setPrefix(b.inbound_subject_prefix || `[${b.code}]`);
  };

  const savePrefix = async (b) => {
    try {
      await businessesApi.updateSettings(b.id, { inbound_subject_prefix: prefix });
      toast.success("Subject prefix updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const visible = isMaster(user) ? businesses : businesses.filter((b) => userBusinessIds(user).includes(b.id));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-4xl font-bold text-espresso" data-testid="inbound-title">Inbound email ingestion</h1>
        <p className="text-espresso-ink/60 mt-2 max-w-2xl">Forward supplier emails to the shared DocHub inbox. DocHub matches the subject prefix per business (editable below), then auto-processes any PDF or image attachment.</p>
      </div>

      <div className="bg-white border border-canvas-border p-6 shadow-soft">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-status-synced/10 flex items-center justify-center text-status-synced rounded-md"><Zap className="w-5 h-5" /></div>
          <div>
            <div className="font-display text-lg font-bold text-espresso">Resend inbound webhook — LIVE</div>
            <div className="text-sm text-espresso-ink/60">DocHub is signed-verified against your Resend webhook secret. Point Resend's inbound domain at DocHub with the URL below.</div>
          </div>
        </div>
        <div className="text-xs uppercase tracking-widest text-espresso-ink/60 mb-1.5 font-semibold">Webhook URL (paste into Resend → Receiving → Webhook)</div>
        <div className="flex items-center gap-3 bg-canvas p-3 border border-canvas-border">
          <code className="font-mono text-xs md:text-sm text-espresso flex-1 truncate" data-testid="webhook-url">{WEBHOOK_URL}</code>
          <button onClick={() => copy(WEBHOOK_URL, "webhook")} className="text-sm inline-flex items-center gap-1.5 text-espresso hover:text-gold shrink-0" data-testid="copy-webhook">
            {copied === "webhook" ? <Check className="w-4 h-4 text-status-synced" /> : <Copy className="w-4 h-4" />} Copy
          </button>
        </div>
        <ol className="mt-4 text-xs text-espresso-ink/70 space-y-1.5 list-decimal list-inside">
          <li>In Resend, add <b>brotherscoffee.co.za</b> as a receiving domain and add the MX records they show you.</li>
          <li>Create inbound address <code className="bg-canvas-border/50 px-1">{ALIAS}</code>.</li>
          <li>Create webhook — paste the URL above, event <code className="bg-canvas-border/50 px-1">email.received</code>.</li>
          <li>Attachments are auto-downloaded, OCR'd and land in the Documents inbox for the matching business.</li>
        </ol>
      </div>

      <div className="bg-white border border-canvas-border p-6 shadow-soft">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gold-soft flex items-center justify-center text-gold rounded-md"><Mail className="w-5 h-5" /></div>
          <div>
            <div className="font-display text-lg font-bold text-espresso">Shared inbound alias</div>
            <div className="text-sm text-espresso-ink/60">Forward or CC supplier invoices here.</div>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-canvas p-4 border border-canvas-border">
          <code className="font-mono text-espresso font-semibold flex-1" data-testid="inbound-alias">{ALIAS}</code>
          <button onClick={() => copy(ALIAS, "alias")} className="text-sm inline-flex items-center gap-1.5 text-espresso hover:text-gold" data-testid="copy-alias">
            {copied === "alias" ? <Check className="w-4 h-4 text-status-synced" /> : <Copy className="w-4 h-4" />} Copy
          </button>
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold text-espresso mb-4">Subject prefixes per business</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((b) => {
            const currentPrefix = b.inbound_subject_prefix || `[${b.code}]`;
            const example = `${currentPrefix} Invoice from Vendor XYZ`;
            const isEditing = editing === b.id;
            return (
              <div key={b.id} className="bg-white border border-canvas-border p-5 shadow-soft" data-testid={`inbound-biz-${b.code}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display font-bold text-espresso">{b.name}</div>
                  {!isEditing && (
                    <button onClick={() => startEdit(b)} className="text-xs text-gold hover:text-gold-light inline-flex items-center gap-1" data-testid={`edit-prefix-${b.code}`}>
                      <Edit2 className="w-3 h-3" /> Edit prefix
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Custom subject prefix (case-insensitive)</label>
                    <div className="flex gap-2">
                      <input value={prefix} onChange={(e) => setPrefix(e.target.value)} data-testid={`prefix-input-${b.code}`} className="flex-1 px-3 py-2 border border-canvas-border text-sm brand-focus font-mono" placeholder={`[${b.code}]`} />
                      <button onClick={() => savePrefix(b)} data-testid={`save-prefix-${b.code}`} className="px-3 py-2 bg-espresso text-canvas text-sm font-semibold hover:bg-espresso-light inline-flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Save</button>
                      <button onClick={() => setEditing(null)} className="px-3 py-2 border border-canvas-border text-sm text-espresso">Cancel</button>
                    </div>
                    <p className="text-xs text-espresso-ink/60">Emails whose subject contains this string will be routed to <b>{b.name}</b>.</p>
                  </div>
                ) : (
                  <>
                    <div className="text-xs uppercase tracking-widest text-espresso-ink/60 mb-1.5">Current prefix</div>
                    <div className="flex items-center gap-2 bg-canvas p-2.5 border border-canvas-border">
                      <code className="font-mono text-sm text-gold font-semibold flex-1">{currentPrefix}</code>
                    </div>
                    <div className="text-xs uppercase tracking-widest text-espresso-ink/60 mt-3 mb-1.5">Example subject</div>
                    <div className="flex items-center gap-2 bg-canvas p-2.5 border border-canvas-border">
                      <code className="font-mono text-xs text-espresso flex-1 truncate">{example}</code>
                      <button onClick={() => copy(example, b.code)} className="text-espresso hover:text-gold">
                        {copied === b.code ? <Check className="w-4 h-4 text-status-synced" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-espresso text-canvas p-8">
        <div className="font-display text-lg font-bold mb-2">How it works</div>
        <ol className="text-sm text-canvas/80 space-y-2 list-decimal list-inside">
          <li>Supplier emails you an invoice, or you forward one to <code className="bg-white/10 px-1">{ALIAS}</code>.</li>
          <li>Add the business subject prefix (default <code className="bg-white/10 px-1">[CODE]</code> — customisable per business above).</li>
          <li>DocHub picks up any PDF/image attachments and starts OCR extraction automatically.</li>
          <li>An Accounting User reviews and publishes to Xero/Zoho from the document detail page.</li>
        </ol>
      </div>
    </div>
  );
}
